import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { UserPlus, Car, CalendarCheck } from 'lucide-react';
import { motion } from 'framer-motion';

export const HowItWorks = () => {
  const { t } = useLanguage();

  const steps = [
    {
      icon: UserPlus,
      number: '01',
      title: t('how_step_1'),
      description: t('how_step_1_desc'),
    },
    {
      icon: Car,
      number: '02',
      title: t('how_step_2'),
      description: t('how_step_2_desc'),
    },
    {
      icon: CalendarCheck,
      number: '03',
      title: t('how_step_3'),
      description: t('how_step_3_desc'),
    },
  ];

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          {...fadeUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight" data-testid="how-title">
            {t('how_title')}
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            {t('how_subtitle')}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line - Desktop */}
          <div className="hidden md:block absolute top-24 left-[calc(16.666%-12px)] right-[calc(16.666%-12px)] h-0.5 bg-slate-200"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="relative text-center"
                data-testid={`step-card-${index}`}
              >
                {/* Step number */}
                <div className="relative z-10 w-12 h-12 mx-auto rounded-full bg-slate-900 flex items-center justify-center mb-6">
                  <span className="text-white font-bold text-sm">{step.number}</span>
                </div>

                {/* Icon */}
                <div className="w-16 h-16 mx-auto rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-6 shadow-sm">
                  <step.icon className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
